import { useEffect, useState } from "react";
import {
  OPTION_TEXT_MAX,
  OptionSide,
  SETUP_MS_PER_DECISION,
} from "@badorbad/shared";
import { AvatarFace } from "../design/AvatarFace";
import { SettingsIcon } from "../design/Icons";
import { Sheet, SheetFrom } from "../design/Sheet";
import { TargetIntro } from "../design/TargetIntro";
import { Split } from "../design/Split";
import { SpiceSegmented } from "../design/SpiceSegmented";
import { TimerBar } from "../design/TimerBar";
import { useSpicePreference } from "../design/useSpicePreference";
import { nameColorFor } from "../design/seed";
import { NAME_COLORS, palette } from "../design/tokens";
import { t, fmt } from "../locale";
import { useGame, selectOpenAssignments } from "../stores/game";

/**
 * The writing screen — the split itself, not a form above it. plan.md §1.4.
 *
 * Built from `design/SetupScreen.tsx`, which was a static preview. The device
 * is unchanged; what is new is that the two halves are fed by the player's own
 * assignments and the suggestion API rather than by a local constant.
 *
 * A player writes one decision per target, so this walks its assignments one at
 * a time and submits each on its own. The server keys decisions by
 * `assignment_index`, never by position, so a submission that crosses with a
 * kick still lands against the right target.
 */

/** Which half a suggestion filled, and the id to credit if it survives. */
type Filled = { a: string | null; b: string | null };

const NO_CREDIT: Filled = { a: null, b: null };

export const Setup = () => {
  const players = useGame((s) => s.players);
  const open = useGame(selectOpenAssignments);
  const assignments = useGame((s) => s.assignments);
  const deadlineAt = useGame((s) => s.setupDeadlineAt);
  const submittedPlayers = useGame((s) => s.submittedPlayers);
  const suggestion = useGame((s) => s.suggestionQueue[0] ?? null);
  const error = useGame((s) => s.error);
  const requestSuggestion = useGame((s) => s.requestSuggestion);
  const clearSuggestion = useGame((s) => s.clearSuggestion);
  const submitDecision = useGame((s) => s.submitDecision);
  const leave = useGame((s) => s.leave);

  const { spice, requestSpice, asking, confirmAdult, cancelAdult } =
    useSpicePreference();
  const [settingsOpen, setSettingsOpen] = useState(false);
  /** Assignment index whose target is still being announced, if any. */
  const [introFor, setIntroFor] = useState<number | null>(null);
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [filled, setFilled] = useState<Filled>(NO_CREDIT);

  const current = open[0] ?? null;

  const settings = (
    <Sheet
      open={settingsOpen}
      onClose={() => setSettingsOpen(false)}
      title={t.setup.settings}
      // The cog lives in the header, so its panel comes down from there.
      from={SheetFrom.Top}
    >
      <SpiceSegmented
        value={spice}
        onChange={requestSpice}
        title={t.setup.spicePrompt}
      />
      <p className="mt-3 text-xs text-ink-dim">{t.setup.spiceNote}</p>
      <button
        className="mt-7 min-h-0 w-full py-2 text-sm underline underline-offset-4"
        style={{ color: palette.sideB }}
        onClick={() => {
          if (leave()) setSettingsOpen(false);
        }}
      >
        {t.lobby.leave}
      </button>
    </Sheet>
  );

  /*
   * Asked once per device, the first time someone reaches for spice 3. It comes
   * up from the bottom rather than from the cog: it is an answer the player
   * gives, not a setting they are adjusting, and it sits over the settings sheet
   * that raised it.
   */
  const adultGate = (
    <Sheet open={asking} onClose={cancelAdult} title={t.setup.adultTitle}>
      <p className="text-sm leading-relaxed text-ink-dim">
        {t.setup.adultBody}
      </p>
      <button
        className="mt-6 w-full bg-side-a px-4 py-4 text-base font-bold uppercase tracking-tight text-bg"
        onClick={confirmAdult}
      >
        {t.setup.adultConfirm}
      </button>
      <button
        className="mt-2 min-h-0 w-full py-3 text-sm text-ink-dim underline underline-offset-4"
        onClick={cancelAdult}
      >
        {t.setup.adultCancel}
      </button>
      <p className="mt-4 text-xs text-ink-dim">{t.setup.adultNote}</p>
    </Sheet>
  );

  // A delivered suggestion drops into the half that asked for it. Its id stays
  // attached while the player edits, as required by plan.md §1.3. Clearing the
  // field or requesting another suggestion replaces that credit.
  useEffect(() => {
    if (!suggestion) return;
    if (suggestion.side === OptionSide.A) {
      setA(suggestion.text);
      setFilled((f) => ({ ...f, a: suggestion.suggestion_id }));
    } else {
      setB(suggestion.text);
      setFilled((f) => ({ ...f, b: suggestion.suggestion_id }));
    }
    clearSuggestion();
  }, [suggestion, clearSuggestion]);

  // Announce each target as its decision comes up, not just the first.
  const currentIndex = current?.index ?? null;
  useEffect(() => {
    if (currentIndex !== null) setIntroFor(currentIndex);
  }, [currentIndex]);

  if (!current) {
    return (
      <div className="relative mx-auto flex h-full max-w-md flex-col">
        {deadlineAt && (
          <TimerBar
            deadlineAt={new Date(deadlineAt).getTime()}
            durationMs={assignments.length * SETUP_MS_PER_DECISION}
          />
        )}
        <button
          onClick={() => setSettingsOpen(true)}
          aria-label={t.setup.settings}
          className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center"
        >
          <SettingsIcon color={palette.ink} />
        </button>
        <div className="flex flex-1 flex-col items-center justify-center px-5 text-center">
          <p className="text-base">{t.setup.waiting}</p>
          <p className="mt-2 text-sm text-ink-dim">
            {fmt(
              t.setup.submitted,
              String(submittedPlayers.length),
              String(players.length),
            )}
          </p>
        </div>
        {settings}
        {adultGate}
      </div>
    );
  }

  const target = players.find((p) => p.user_id === current.target_id);
  const targetName = target?.display_name ?? "";
  const targetSeed = target?.avatar_seed ?? "";

  const trimmedA = a.trim();
  const trimmedB = b.trim();
  const ready =
    trimmedA.length > 0 && trimmedB.length > 0 && trimmedA !== trimmedB;

  const done = assignments.length - open.length;

  const submit = () => {
    if (!ready) return;
    submitDecision({
      assignment_index: current.index,
      option_a: trimmedA,
      option_b: trimmedB,
      suggestion_a_id: filled.a,
      suggestion_b_id: filled.b,
    });
    // Cleared optimistically: the next assignment needs empty halves, and a
    // rejected submission leaves its index open, so the same one comes back.
    setA("");
    setB("");
    setFilled(NO_CREDIT);
  };

  return (
    <div className="relative mx-auto flex h-full max-w-md flex-col overflow-hidden">
      {deadlineAt && (
        <TimerBar
          deadlineAt={new Date(deadlineAt).getTime()}
          // The window the server opened: two minutes per decision (§1.2). The
          // bar needs the full span to show a fraction, not just the remainder.
          durationMs={assignments.length * SETUP_MS_PER_DECISION}
        />
      )}

      {/* Where the announcement lands, and what it leaves behind. */}
      <div
        className="flex items-center gap-3 px-3 py-3 transition-opacity duration-300"
        style={{ opacity: introFor === null ? 1 : 0 }}
      >
        <AvatarFace seed={targetSeed} size={38} />
        <div className="flex-1">
          <div className="text-base font-bold uppercase leading-none tracking-tight">
            {t.setup.writeFor}{" "}
            <span style={{ color: nameColorFor(targetSeed, NAME_COLORS) }}>
              {targetName}
            </span>
          </div>
          <div className="mt-1.5 text-xs text-ink">
            {fmt(
              t.setup.decisionCount,
              String(done + 1),
              String(assignments.length),
            )}
          </div>
        </div>

        <button
          onClick={() => setSettingsOpen(true)}
          aria-label={t.setup.settings}
          className="flex h-11 w-11 shrink-0 items-center justify-center"
        >
          <SettingsIcon color={palette.ink} />
        </button>
      </div>

      <div className="flex-1 overflow-hidden">
        <Split
          mode="write"
          maxLength={OPTION_TEXT_MAX}
          a={{
            value: a,
            onChange: (v) => {
              setA(v);
            },
            onRandom: () => requestSuggestion(spice, OptionSide.A),
            onClear: () => {
              setA("");
              setFilled((f) => ({ ...f, a: null }));
            },
          }}
          b={{
            value: b,
            onChange: (v) => {
              setB(v);
            },
            onRandom: () => requestSuggestion(spice, OptionSide.B),
            onClear: () => {
              setB("");
              setFilled((f) => ({ ...f, b: null }));
            },
          }}
        />
      </div>

      <div>
        <button
          data-sound="submit"
          disabled={!ready}
          onClick={submit}
          className="w-full py-4 text-sm font-bold uppercase tracking-widest disabled:opacity-40"
          style={{ backgroundColor: palette.ink, color: palette.bg }}
        >
          {t.setup.submit}
        </button>

        <div className="flex items-center justify-between px-3 py-3">
          <span className="text-xs text-ink">
            {error ??
              (ready
                ? fmt(
                    t.setup.submitted,
                    String(submittedPlayers.length),
                    String(players.length),
                  )
                : t.setup.needBoth)}
          </span>
          <div className="flex gap-1.5">
            {players.map((p) => (
              <span
                key={p.user_id}
                style={{ opacity: submittedPlayers.includes(p.user_id) ? 1 : 0.3 }}
              >
                <AvatarFace seed={p.avatar_seed} size={20} />
              </span>
            ))}
          </div>
        </div>
      </div>

      {introFor !== null && (
        <TargetIntro
          key={introFor}
          seed={targetSeed}
          name={targetName}
          onDone={() => setIntroFor(null)}
        />
      )}

      {settings}
      {adultGate}
    </div>
  );
};
